import React, {useEffect, useRef, useState} from "react";
import {useSelector} from "../../../shared-services/redux-tiny";
import Item from "./CartSidebar.Item";
import currentTheme from "../../../react-themes/theme";
import Adjustments from "./CartSidebar.Adjustments";
import CTAButton from "./CartSidebar.CTAButton";
import NextDelivery from "./CartSidebar.NextDelivery";
import useMainContainerBoundaries from "../../shared/useMainContainerBoundaries";
import globalState from "../../../shared-services/globalState";

const theme = currentTheme();

const CartSidebar = (props) => {
  const defaultHeight = 250;
  const marginBottom = 20;
  const marginTop = 20;
  const transitionDuration = theme.transitionTime.fast;

  const {className = "", style} = props;
  const {currentCart} = useSelector(state => state.cart);
  const {currency} = useSelector(state => state.session);

  const [firstLoad, setFirstLoad] = useState(true);

  const {boundaries} = useMainContainerBoundaries();

  const {getGlobalState} = globalState();
  const {rightSidebarLayout, setRightSidebarLayout} = getGlobalState("layout");

  const itemsRef = useRef();

  const [visibleItems, setVisibleItems] = useState(currentCart?.line_items || []);
  const [maxHeight, setMaxHeight] = useState(rightSidebarLayout?.height || defaultHeight);
  const [transitionTimeout, setTransitionTimeout] = useState(null);

  const {
    value: widthValue
  } = theme.cartSidebar.defaultWidth;

  const setHeight = () => {
    if (!itemsRef?.current) return;

    const {bottomPosition = 0} = boundaries;
    const windowHeight = Math.min(window.innerHeight, bottomPosition || window.innerHeight);
    const elementPositionY = itemsRef?.current?.getBoundingClientRect()?.y;
    
    if (!elementPositionY) return;

    const newHeight = windowHeight - elementPositionY - marginBottom;
    if (newHeight === maxHeight) return;

    setMaxHeight(newHeight);
    setRightSidebarLayout({height: newHeight});
  };

  useEffect(() => {
    if (transitionTimeout) clearTimeout(transitionTimeout);

    const newTimeout = setTimeout(() => {
      requestAnimationFrame(setHeight);
    }, transitionDuration);

    setTransitionTimeout(newTimeout);
  }, [boundaries]);

  useEffect(() => {
    if (currentCart && firstLoad) setFirstLoad(false);

    // We use product_id for integrity, since item.id will not be present until response from the server.
    const visibleIDs = visibleItems?.map(item => item?.product_id) || [];
    const currentCartIds = currentCart?.line_items?.map(item => item.product_id) || [];

    const erasedItemsIDs = visibleIDs.filter(id => !currentCartIds.includes(id));

    if (erasedItemsIDs?.length) {
      visibleItems.forEach(item => {
        if (erasedItemsIDs.includes(item.product_id)) item.remove = true;
      });
    }

    if (erasedItemsIDs?.length && currentCartIds?.length) {
      setVisibleItems(visibleItems);
      setTimeout(() => setVisibleItems(currentCart?.line_items), transitionDuration);
    } else setVisibleItems(currentCart?.line_items);
  }, [currentCart]);

  return <div
    className={`
      cart-sidebar
      ${className}`}
    style={{
      ...style
    }}
  >
    <div
      className={`
        overflow-x-hidden
        background-color-white
        border-radius-md
        transition-all-default
        ${props?.fixed ? "position-fixed" : null}
      `}
      style={{
        width: visibleItems?.length || !props?.fixed ? widthValue : 0,
        position: props?.fixed ? "fixed" : null,
        top: `${boundaries?.topPosition + marginTop}px`
      }}
    >
      <div style={{width: widthValue}}>
        <CTAButton currentCart={currentCart} currency={currency}/>

        <Adjustments currentCart={currentCart} className="margin-top-5"/>

        <NextDelivery className="margin-top-15"/>

        <div
          ref={itemsRef}
          className={`
            flex
            flex-direction-column
            gap-10
            margin-top-15
            overflow-auto
            transition-all-default
          `}
          style={{
            height: props?.fixed ? `${maxHeight}px` : null
          }}
        >
          {visibleItems?.length > 0 && visibleItems
            .map(item => <Item lineItem={item} firstLoad={firstLoad} key={item?.product_id}/>)
          }
        </div>
      </div>
    </div>
  </div>;
};

export default CartSidebar;
